import { Link } from "react-router-dom";
import { Clock, MapPin, Accessibility } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { CategoryPill, DemoBadge, Img } from "@/components/common";
import FavButton from "@/components/FavButton";
import { fmtDuration, isLive, timeOf, endOf } from "@/lib/time";

export function StatusTag({ act }) {
  if (act.status === "cancelada") return <span className="rounded-full bg-red-700 text-white px-2.5 py-1 text-xs font-bold">Cancelada</span>;
  if (act.status === "alterada") return <span className="rounded-full bg-amber-500 text-[var(--ink)] px-2.5 py-1 text-xs font-bold">Alterada</span>;
  return null;
}

export function LiveTag() {
  return <span className="inline-flex items-center gap-1.5 rounded-full bg-white text-red-700 px-2.5 py-1 text-xs font-bold"><span className="h-2 w-2 rounded-full bg-red-600 live-dot" />A decorrer</span>;
}

export default function ActivityCard({ act }) {
  const site = useSite();
  const cat = site.cats[act.category_id];
  const live = isLive(act, site.now);
  return (
    <Link to={`/atividade/${act.slug}`} data-testid={`activity-card-${act.slug}`}
      className="group flex flex-col bg-[var(--ink)] text-white rounded-2xl overflow-hidden">
      <div className="relative aspect-[4/3] overflow-hidden">
        <Img src={act.image} position={act.image_position} alt="" className="h-full w-full object-cover" />
        <div className="absolute top-3 left-3 flex flex-wrap gap-2">{live && <LiveTag />}<StatusTag act={act} /></div>
        <div className="absolute top-3 right-3"><FavButton act={act} /></div>
      </div>
      <div className="p-5 flex flex-col gap-3 flex-1">
        <div className="flex items-end gap-3 font-cond uppercase">
          <span className="text-4xl font-bold leading-none">{act.start.slice(8, 10)}</span>
          <span className="text-sm leading-tight font-semibold text-white/80">Out<br />{timeOf(act.start)}–{timeOf(endOf(act))}</span>
        </div>
        <p className="eyebrow text-[var(--sun)]">{cat?.name}</p>
        <h3 className="font-display text-lg sm:text-xl font-extrabold uppercase leading-tight tracking-tight">{act.title}</h3>
        <DemoBadge show={act.demo} className="self-start" />
        <div className="mt-auto flex flex-wrap gap-x-4 gap-y-1 text-sm text-white/75">
          <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" aria-hidden />{site.placeOf(act)}</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" aria-hidden />{fmtDuration(act.duration)}</span>
          {act.accessibility?.length > 0 && <span className="inline-flex items-center gap-1" title={act.accessibility.join(", ")}><Accessibility className="h-4 w-4" aria-hidden />{act.accessibility.length}<span className="sr-only">recursos de acessibilidade</span></span>}
        </div>
      </div>
    </Link>
  );
}

export function ActivityRow({ act, conflict }) {
  const site = useSite();
  const cat = site.cats[act.category_id];
  const live = isLive(act, site.now);
  return (
    <Link to={`/atividade/${act.slug}`} data-testid={`activity-row-${act.slug}`}
      className={`group grid grid-cols-[64px_1fr_auto] sm:grid-cols-[96px_1fr_auto] gap-4 items-start py-5 px-1 sm:px-2 bg-white border-b transition-colors hover:bg-slate-50 ${conflict ? "border-l-4 border-l-amber-500 bg-amber-50/60 pl-3" : "border-black/10"}`}>
      <div className="font-mono-x">
        <p className="text-lg sm:text-xl font-semibold">{timeOf(act.start)}</p>
        <p className="text-xs text-slate-500">até {timeOf(endOf(act))}</p>
      </div>
      <div className="min-w-0">
        <div className="flex flex-wrap gap-2 mb-2">{live && <span className="inline-flex items-center gap-1.5 text-xs font-bold text-red-700"><span className="h-2 w-2 rounded-full bg-red-600 live-dot" />A decorrer</span>}<StatusTag act={act} /></div>
        <h3 className="font-display text-lg sm:text-xl font-bold leading-snug group-hover:text-[var(--leaf)]">{act.title}</h3>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-600">
          <CategoryPill cat={cat} /><span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" aria-hidden />{site.placeOf(act)}</span><DemoBadge show={act.demo} />
        </div>
        {conflict && <p className="mt-2 text-sm font-semibold text-amber-800" data-testid={`conflict-warning-${act.slug}`}>Horário sobreposto com: {conflict}</p>}
      </div>
      <FavButton act={act} />
    </Link>
  );
}
